import { useTranslation } from "react-i18next"

import type { FairUseOverviewFilter } from "@/api/admin/fair-use"

import { Inbox, SearchX, Users } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"

type FairUseOpsEmptyProps = {
  filter: FairUseOverviewFilter
  search: string
  onClearSearch: () => void
  onShowAll: () => void
  onTopUp: () => void
}

/**
 * The three ways the table can be empty, each with the one action that gets
 * the admin out of it.
 *
 * "Nothing waiting for you" is a GOOD state — the inbox is clear — so it is
 * never dressed as an error, and it still offers a way back to the full list
 * rather than leaving the admin on a dead screen.
 */
export function FairUseOpsEmpty({
  filter,
  search,
  onClearSearch,
  onShowAll,
  onTopUp,
}: FairUseOpsEmptyProps) {
  const { t } = useTranslation()

  if (search) {
    return (
      <EmptyState
        icon={SearchX}
        title={t("adminDashboard.fairUse.ops.empty.searchTitle", "Nobody matches that")}
        description={t(
          "adminDashboard.fairUse.ops.empty.searchDescription",
          "No loaded user matches “{{query}}”.",
          { query: search }
        )}
        primaryAction={
          <Button className="h-10" variant="outline" onClick={onClearSearch}>
            {t("adminDashboard.fairUse.ops.empty.clearSearch", "Clear search")}
          </Button>
        }
      />
    )
  }

  if (filter === "pending") {
    return (
      <EmptyState
        icon={Inbox}
        title={t("adminDashboard.fairUse.ops.empty.pendingTitle", "Nothing waiting for you")}
        description={t(
          "adminDashboard.fairUse.ops.empty.pendingDescription",
          "Nobody has asked for more budget this month."
        )}
        primaryAction={
          <Button className="h-10" variant="outline" onClick={onShowAll}>
            {t("adminDashboard.fairUse.ops.empty.showAll", "Show all users")}
          </Button>
        }
      />
    )
  }

  if (filter !== "all") {
    return (
      <EmptyState
        icon={Users}
        title={t("adminDashboard.fairUse.ops.empty.filterTitle", "Nobody matches this filter")}
        description={t(
          "adminDashboard.fairUse.ops.empty.filterDescription",
          "No user in this month matches the selected view."
        )}
        primaryAction={
          <Button className="h-10" variant="outline" onClick={onShowAll}>
            {t("adminDashboard.fairUse.ops.empty.showAll", "Show all users")}
          </Button>
        }
      />
    )
  }

  return (
    <EmptyState
      icon={Users}
      title={t("adminDashboard.fairUse.ops.empty.title", "No usage this month")}
      description={t(
        "adminDashboard.fairUse.ops.empty.description",
        "Nobody has used their AI budget for this month yet."
      )}
      primaryAction={
        <Button className="h-10" onClick={onTopUp}>
          {t("adminDashboard.fairUse.ops.topUp", "Top up a user")}
        </Button>
      }
    />
  )
}
